import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Terminal, Monitor, Smartphone, Copy, Check, Download, ShieldAlert, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PublicDownloadPage = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Fetch Links
  const { data: links = {} } = useQuery({
    queryKey: ['publicLinks'],
    queryFn: async () => {
        const res = await axiosClient.get('/public/downloads');
        return res.data || res;
    }
  });

  const installCmd = `curl -sL ${links.linux || 'https://...'} | sudo bash -s -- --license YOUR_KEY`;

  const copyCmd = () => {
      navigator.clipboard.writeText(installCmd);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
  };


  return (
    <div className="min-h-screen bg-[#020617] pt-24 pb-20 px-6">
      <div className="max-w-6xl mx-auto">
        
        <div className="text-center mb-16 space-y-4">
          <h1 className="text-4xl md:text-5xl font-black text-white">Deploy <span className="text-teal-500">Sentinel</span></h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Choose your platform. Installation takes less than 30 seconds.
          </p>
        </div>

        {/* 1. KEY INSTRUCTION */}
        <div className="bg-gradient-to-r from-blue-900/40 to-slate-900 border border-blue-500/30 p-6 rounded-2xl mb-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-500/20 rounded-xl text-teal-400">
                    <Key className="w-8 h-8"/>
                </div>
                <div>
                    <h3 className="text-xl font-bold text-white">Requirement: License Key</h3>
                    <p className="text-slate-400 text-sm mt-1">
                        You need a valid License Key to activate the agent during installation.
                        <br/>Don't have one? Create an account to get a Free Trial.
                    </p>
                </div>
            </div>
            <button onClick={() => navigate('/register')} className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-xl font-bold whitespace-nowrap shadow-lg">
                Get License Key
            </button>
        </div>

        {/* 2. PLATFORMS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* LINUX (Main) */}
            <div className="bg-[#0f172a] border border-slate-700 rounded-2xl p-6 md:col-span-2 lg:col-span-2 shadow-xl">
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-orange-500/10 text-orange-500 rounded-xl flex items-center justify-center">
                        <Terminal className="w-6 h-6"/>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-white">Linux Server</h3>
                        <p className="text-slate-500 text-sm">Ubuntu, Debian, CentOS, RHEL</p>
                    </div>
                </div>
                
                <div className="bg-black/50 border border-slate-800 rounded-xl p-4 font-mono text-sm text-green-400 relative group">
                    <div className="overflow-x-auto pb-2 pr-12">{installCmd}</div>
                    <button 
                        onClick={copyCmd} 
                        className="absolute top-2 right-2 p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 transition-colors"
                    >
                        {copied ? <Check className="w-4 h-4 text-green-500"/> : <Copy className="w-4 h-4"/>}
                    </button>
                </div>
                <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                    <ShieldAlert className="w-3 h-3 text-yellow-500"/>
                    <span>Replace <span className="text-white font-mono">YOUR_KEY</span> with your actual license key.</span>
                </div>
            </div>

            {/* WINDOWS */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-6 opacity-60 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
                    <span className="bg-slate-800 text-slate-300 px-3 py-1 rounded-full text-xs font-bold border border-slate-600">COMING SOON</span>
                </div>
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-blue-500/10 text-teal-400 rounded-xl flex items-center justify-center">
                        <Monitor className="w-6 h-6"/>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-white">Windows</h3>
                        <p className="text-slate-500 text-sm">Server 2019+, Windows 10/11</p>
                    </div>
                </div>
                <button disabled className="w-full border border-slate-700 text-slate-500 py-3 rounded-xl font-bold flex items-center justify-center gap-2">
                    <Download className="w-4 h-4"/> Download .exe
                </button>
            </div>

            {/* ANDROID */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-6 opacity-60 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
                    <span className="bg-slate-800 text-slate-300 px-3 py-1 rounded-full text-xs font-bold border border-slate-600">COMING SOON</span>
                </div>
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-green-500/10 text-green-500 rounded-xl flex items-center justify-center">
                        <Smartphone className="w-6 h-6"/>
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-white">Android</h3>
                        <p className="text-slate-500 text-sm">Monitor mobile fleets</p>
                    </div>
                </div>
                <button disabled className="w-full border border-slate-700 text-slate-500 py-3 rounded-xl font-bold flex items-center justify-center gap-2">
                    <Download className="w-4 h-4"/> Get APK
                </button>
            </div>

        </div>
      </div>
    </div>
  );
};

export default PublicDownloadPage;